import React, { useEffect, useState } from 'react';
import { FileText, ImageIcon, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { api } from '../../../utils/api';
import { ROOM_COLORS } from './model3d';

const FloorSVG = ({ model, floor, big }) => {
  const { width: W, depth: D } = model.footprint;
  return (
    <svg viewBox={`-1 -1 ${W + 2} ${D + 2}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      <rect x={0} y={0} width={W} height={D} fill="#fff" stroke="#1c1a18" strokeWidth={Math.max(W, D) / 140} />
      {floor.rooms.map((r, i) => (
        <g key={i}>
          <rect x={r.x} y={r.y} width={r.w} height={r.d} fill={ROOM_COLORS[r.type]} fillOpacity={0.45} stroke="#3a3632" strokeWidth={Math.max(W, D) / 400} />
          {big && r.w * r.d > 8 && <text x={r.x + r.w / 2} y={r.y + r.d / 2} fontSize={Math.max(W, D) / 70} textAnchor="middle" fill="#1c1a18">{r.name}</text>}
        </g>
      ))}
    </svg>
  );
};

const FileThumb = ({ planId, file, onOpen }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (!file.available || !['png', 'jpg', 'jpeg', 'webp', 'pdf'].includes(file.ext)) return undefined;
    api.getPlanFile(planId, file.index, file.ext === 'pdf').then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [planId, file]);
  return (
    <button onClick={() => onOpen(file)} disabled={!file.available} data-testid={`blueprint-file-${file.index}`}
      className="group rounded-xl border border-stone-200 bg-stone-50 p-2 text-left hover:border-[#F26B1F] transition-colors disabled:opacity-50">
      <div className="aspect-[4/3] rounded-lg bg-white overflow-hidden flex items-center justify-center">
        {src ? <img src={src} alt={file.name} className="w-full h-full object-contain" /> : (file.ext === 'pdf' ? <FileText className="text-stone-400" /> : <ImageIcon className="text-stone-400" />)}
      </div>
      <p className="mt-1.5 text-[11px] font-medium text-stone-600 flex items-center gap-1">{file.name} · {file.ext.toUpperCase()}{file.available && <ExternalLink size={10} className="opacity-0 group-hover:opacity-100" />}</p>
      {!file.available && <p className="text-[10px] text-stone-400">Filen finns inte längre</p>}
    </button>
  );
};

export const BlueprintPanel = ({ planId, model }) => {
  const [tab, setTab] = useState('plans');
  const [files, setFiles] = useState([]);
  const [zoom, setZoom] = useState(null);
  useEffect(() => { api.listPlanFiles(planId).then((r) => setFiles(r.data?.files || [])).catch(() => setFiles([])); }, [planId]);

  const openFile = async (f) => {
    const r = await api.getPlanFile(planId, f.index).catch(() => null);
    if (r) window.open(URL.createObjectURL(r.data), '_blank');
  };

  return (
    <div className="op-card p-4 h-full flex flex-col" data-testid="blueprint-panel">
      <div className="flex gap-5 border-b border-stone-200 text-[13px] font-semibold">
        {[['plans', 'Planritningar'], ['files', `Uppladdade (${files.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`blueprint-tab-${k}`}
            className={`pb-2.5 -mb-px border-b-2 transition-colors ${tab === k ? 'border-[#F26B1F] text-stone-900' : 'border-transparent text-stone-400 hover:text-stone-700'}`}>{l}</button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto pt-3 grid grid-cols-2 gap-2.5 content-start">
        {tab === 'plans' && (model ? model.floors.map((f) => (
          <button key={f.level} onClick={() => setZoom(f)} data-testid={`blueprint-floor-${f.level}`}
            className={`rounded-xl border p-2 text-left transition-colors hover:border-[#F26B1F] ${f.level === 1 ? 'border-[#F26B1F] bg-[#F26B1F]/[0.04]' : 'border-stone-200'}`}>
            <div className="aspect-[4/3] rounded-lg bg-white overflow-hidden"><FloorSVG model={model} floor={f} /></div>
            <p className="mt-1.5 text-[11px] font-medium text-stone-600 truncate">{f.name}</p>
          </button>
        )) : <p className="col-span-2 text-xs text-stone-400 py-6 text-center">Planritningar visas när 3D-modellen är klar.</p>)}
        {tab === 'files' && (files.length ? files.map((f) => <FileThumb key={f.index} planId={planId} file={f} onOpen={openFile} />)
          : <p className="col-span-2 text-xs text-stone-400 py-6 text-center">Inga ritningar uppladdade för denna kalkyl.</p>)}
      </div>
      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{zoom?.name}</DialogTitle></DialogHeader>
          {zoom && <div className="h-[60vh]" data-testid="blueprint-floor-zoom"><FloorSVG model={model} floor={zoom} big /></div>}
        </DialogContent>
      </Dialog>
    </div>
  );
};
